import type { UserUuid } from "../identity";
// MedicalHistoryTypes.ts - Define all possible medical history types and their fields

export interface MedicalHistory {
	id: number;
	patient_id: UserUuid;
	history_type: string;
	description?: string;
	details?: string | any; // JSON object for additional details
	created_at?: string;
	updated_at?: string;
	is_active: boolean;
	severity: string | null;
	date_identified: Date | null;
	date_resolved: Date | null;
	last_review_date: Date | null;
	next_review_date: Date | null;
	primary_provider_id: UserUuid | null;
	provider?: {
		id: UserUuid;
		first_name: string;
		last_name: string;
	};
}

export interface MedicalHistoryStats {
	total_records: number;
	active_records: number;
	resolved_records: number;
	critical_records: number;
	records_by_type: Record<string, number>;
	records_by_severity: Record<string, number>;
	records_needing_review: number;
}


export const historyTypesOptions = [
	// Current Types (Enhanced)
	{ label: "Diagnosis", value: "diagnosis" },
	{ label: "Lifestyle", value: "lifestyle" },
	{ label: "Allergy", value: "allergy" },

	// Medical History
	{ label: "Chronic Condition", value: "chronic_condition" },
	{ label: "Acute Condition", value: "acute_condition" },
	{ label: "Infectious Disease", value: "infectious_disease" },

	// Surgical History
	{ label: "Surgical Procedure", value: "surgical_procedure" },
	{ label: "Medical Procedure", value: "medical_procedure" },
	{ label: "Implant", value: "implant" },

	// Family History
	{ label: "Family Health History", value: "family_history" },
	{ label: "Genetic Predisposition", value: "genetic_predisposition" },

	// Preventive Care
	{ label: "Immunization", value: "immunization" },
	{ label: "Screening Test", value: "screening_test" },
	{ label: "Preventive Measure", value: "preventive_measure" },

	// Social History
	{ label: "Substance Use", value: "substance_use" },
	{ label: "Environmental Exposure", value: "environmental_exposure" },
	{ label: "Occupational Hazard", value: "occupational_hazard" },
	{ label: "Travel History", value: "travel_history" },

	// Mental Health
	{ label: "Mental Health Condition", value: "mental_health" },
	{ label: "Psychiatric Treatment", value: "psychiatric_treatment" },
	{ label: "Cognitive Assessment", value: "cognitive_assessment" },

	// Reproductive Health
	{ label: "Pregnancy", value: "pregnancy" },
	{ label: "Obstetric History", value: "obstetric_history" },
	{ label: "Gynecological Condition", value: "gynecological_condition" },
	{ label: "Contraception", value: "contraception" },

	// Pediatric History
	{ label: "Developmental Milestone", value: "developmental_milestone" },
	{ label: "Congenital Condition", value: "congenital_condition" },
	{ label: "Growth Assessment", value: "growth_assessment" },

	// Special Categories
	{ label: "Trauma/Injury", value: "trauma_injury" },
	{ label: "Hospitalization", value: "hospitalization" },
	{ label: "Emergency Visit", value: "emergency_visit" },
	{ label: "Functional Assessment", value: "functional_assessment" },
	{ label: "Nutritional Assessment", value: "nutritional_assessment" },
	{ label: "Sleep Assessment", value: "sleep_assessment" },
	{ label: "Pain Assessment", value: "pain_assessment" },
	{ label: "Rehabilitation", value: "rehabilitation" },
];

// Define field types for reuse
const commonFields = {
	dateField: { name: "date", label: "Date", type: "date" },
	notesField: { name: "notes", label: "Notes", type: "text" },
	severityOptions: [
		{ label: "Mild", value: "mild" },
		{ label: "Moderate", value: "moderate" },
		{ label: "Severe", value: "severe" },
		{ label: "Life-threatening", value: "life_threatening" },
	],
	statusOptions: [
		{ label: "Active", value: "active" },
		{ label: "Resolved", value: "resolved" },
		{ label: "In Remission", value: "remission" },
		{ label: "Chronic", value: "chronic" },
		{ label: "Recurrent", value: "recurrent" },
	],
	treatmentStatusOptions: [
		{ label: "Not Started", value: "not_started" },
		{ label: "In Progress", value: "in_progress" },
		{ label: "Completed", value: "completed" },
		{ label: "Discontinued", value: "discontinued" },
	],
	familyMemberOptions: [
		{ label: "Mother", value: "mother" },
		{ label: "Father", value: "father" },
		{ label: "Sister", value: "sister" },
		{ label: "Brother", value: "brother" },
		{ label: "Daughter", value: "daughter" },
		{ label: "Son", value: "son" },
		{ label: "Maternal Grandmother", value: "maternal_grandmother" },
		{ label: "Maternal Grandfather", value: "maternal_grandfather" },
		{ label: "Paternal Grandmother", value: "paternal_grandmother" },
		{ label: "Paternal Grandfather", value: "paternal_grandfather" },
		{ label: "Aunt", value: "aunt" },
		{ label: "Uncle", value: "uncle" },
		{ label: "Cousin", value: "cousin" },
		{ label: "Other", value: "other" },
	],
	yesNoOptions: [
		{ label: "Yes", value: true },
		{ label: "No", value: false },
	],
	frequencyOptions: [
		{ label: "Daily", value: "daily" },
		{ label: "Weekly", value: "weekly" },
		{ label: "Monthly", value: "monthly" },
		{ label: "Yearly", value: "yearly" },
		{ label: "As Needed", value: "as_needed" },
		{ label: "Other", value: "other" },
	],
};

// Comprehensive mapping of history types to required fields
export const detailFieldsMapping = {
	// Diagnosis
	diagnosis: [
		{ name: "condition_name", label: "Condition Name", type: "text" },
		{ name: "diagnosis_date", label: "Diagnosis Date", type: "date" },
		{ name: "diagnosing_provider", label: "Diagnosing Provider", type: "text" },
		{ name: "diagnosed_at", label: "Diagnosed At (Facility)", type: "text" },
		{ name: "status", label: "Current Status", type: "dropdown", options: commonFields.statusOptions },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "treatment_plan", label: "Treatment Plan", type: "textarea" },
		{ name: "followup_required", label: "Follow-up Required", type: "boolean" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "icd_code", label: "ICD Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Lifestyle
	lifestyle: [
		{
			name: "category", label: "Category", type: "dropdown", options: [
				{ label: "Exercise", value: "exercise" },
				{ label: "Diet", value: "diet" },
				{ label: "Sleep", value: "sleep" },
				{ label: "Smoking", value: "smoking" },
				{ label: "Alcohol", value: "alcohol" },
				{ label: "Recreational Drugs", value: "recreational_drugs" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "from_date", label: "From Date", type: "date" },
		{ name: "to_date", label: "To Date", type: "date" },
		{ name: "description", label: "Description", type: "textarea" },
		{ name: "frequency", label: "Frequency", type: "dropdown", options: commonFields.frequencyOptions },
		{ name: "quantity", label: "Quantity", type: "text" },
		{ name: "units", label: "Units", type: "text" },
		{ name: "impact_on_health", label: "Impact on Health", type: "textarea" },
		{ name: "recommended_changes", label: "Recommended Changes", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Allergy
	allergy: [
		{ name: "allergen", label: "Allergen", type: "text" },
		{
			name: "allergen_type", label: "Allergen Type", type: "dropdown", options: [
				{ label: "Medication", value: "medication" },
				{ label: "Food", value: "food" },
				{ label: "Environmental", value: "environmental" },
				{ label: "Insect", value: "insect" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "reaction", label: "Reaction", type: "textarea" },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "date_identified", label: "Date Identified", type: "date" },
		{ name: "diagnosis_method", label: "Diagnosis Method", type: "text" },
		{ name: "treatment_instructions", label: "Treatment Instructions", type: "textarea" },
		{ name: "emergency_instructions", label: "Emergency Instructions", type: "textarea" },
		{ name: "has_epipen", label: "Has EpiPen", type: "boolean" },
		{ name: "has_medical_alert", label: "Has Medical Alert ID/Bracelet", type: "boolean" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Chronic Condition
	chronic_condition: [
		{ name: "condition_name", label: "Condition Name", type: "text" },
		{ name: "onset_date", label: "Onset Date", type: "date" },
		{ name: "diagnosis_date", label: "Diagnosis Date", type: "date" },
		{ name: "status", label: "Current Status", type: "dropdown", options: commonFields.statusOptions },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "managing_provider", label: "Managing Provider", type: "text" },
		{ name: "specialty", label: "Provider Specialty", type: "text" },
		{ name: "current_treatment", label: "Current Treatment", type: "textarea" },
		{ name: "treatment_start_date", label: "Treatment Start Date", type: "date" },
		{ name: "disease_progression", label: "Disease Progression", type: "textarea" },
		{ name: "functional_limitation", label: "Functional Limitation", type: "textarea" },
		{ name: "monitoring_plan", label: "Monitoring Plan", type: "textarea" },
		{ name: "icd_code", label: "ICD Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Acute Condition
	acute_condition: [
		{ name: "condition_name", label: "Condition Name", type: "text" },
		{ name: "onset_date", label: "Onset Date", type: "date" },
		{ name: "resolution_date", label: "Resolution Date", type: "date" },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "treating_provider", label: "Treating Provider", type: "text" },
		{ name: "treatment_provided", label: "Treatment Provided", type: "textarea" },
		{ name: "hospitalization_required", label: "Hospitalization Required", type: "boolean" },
		{ name: "complication", label: "Complications", type: "textarea" },
		{ name: "followup_needed", label: "Follow-up Needed", type: "boolean" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "icd_code", label: "ICD Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Infectious Disease
	infectious_disease: [
		{ name: "disease_name", label: "Disease Name", type: "text" },
		{ name: "pathogen", label: "Pathogen", type: "text" },
		{ name: "onset_date", label: "Onset Date", type: "date" },
		{ name: "diagnosis_date", label: "Diagnosis Date", type: "date" },
		{ name: "diagnosis_method", label: "Diagnosis Method", type: "text" },
		{ name: "resolution_date", label: "Resolution Date", type: "date" },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "is_contagious", label: "Is Currently Contagious", type: "boolean" },
		{ name: "isolation_required", label: "Isolation Required", type: "boolean" },
		{ name: "isolation_end_date", label: "Isolation End Date", type: "date" },
		{ name: "treatment_administered", label: "Treatment Administered", type: "textarea" },
		{ name: "reportable_disease", label: "Reportable Disease", type: "boolean" },
		{ name: "reported_to_authorities", label: "Reported to Authorities", type: "boolean" },
		{ name: "report_date", label: "Report Date", type: "date" },
		{ name: "exposed_contacts", label: "Exposed Contacts", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Surgical Procedure
	surgical_procedure: [
		{ name: "procedure_name", label: "Procedure Name", type: "text" },
		{ name: "procedure_date", label: "Procedure Date", type: "date" },
		{ name: "surgeon", label: "Surgeon", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "reason", label: "Reason for Procedure", type: "textarea" },
		{
			name: "approach", label: "Surgical Approach", type: "dropdown", options: [
				{ label: "Open", value: "open" },
				{ label: "Laparoscopic", value: "laparoscopic" },
				{ label: "Robotic", value: "robotic" },
				{ label: "Endoscopic", value: "endoscopic" },
				{ label: "Arthroscopic", value: "arthroscopic" },
				{ label: "Other", value: "other" },
			]
		},
		{
			name: "anesthesia_type", label: "Anesthesia Type", type: "dropdown", options: [
				{ label: "General", value: "general" },
				{ label: "Regional", value: "regional" },
				{ label: "Local", value: "local" },
				{ label: "Sedation", value: "sedation" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "outcome", label: "Outcome", type: "textarea" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{ name: "recovery_notes", label: "Recovery Notes", type: "textarea" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "has_operative_report", label: "Has Operative Report", type: "boolean" },
		{ name: "has_pathology", label: "Has Pathology Report", type: "boolean" },
		{ name: "cpt_code", label: "CPT Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Medical Procedure
	medical_procedure: [
		{ name: "procedure_name", label: "Procedure Name", type: "text" },
		{ name: "procedure_date", label: "Procedure Date", type: "date" },
		{ name: "provider", label: "Provider", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "reason", label: "Reason for Procedure", type: "textarea" },
		{
			name: "procedure_type", label: "Procedure Type", type: "dropdown", options: [
				{ label: "Diagnostic", value: "diagnostic" },
				{ label: "Therapeutic", value: "therapeutic" },
				{ label: "Preventive", value: "preventive" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "body_site", label: "Body Site", type: "text" },
		{ name: "result", label: "Result/Outcome", type: "textarea" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{ name: "followup_needed", label: "Follow-up Needed", type: "boolean" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "cpt_code", label: "CPT Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Implant
	implant: [
		{
			name: "implant_type", label: "Implant Type", type: "dropdown", options: [
				{ label: "Orthopedic", value: "orthopedic" },
				{ label: "Cardiac", value: "cardiac" },
				{ label: "Neurological", value: "neurological" },
				{ label: "Dental", value: "dental" },
				{ label: "Cosmetic", value: "cosmetic" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "implant_name", label: "Implant Name", type: "text" },
		{ name: "manufacturer", label: "Manufacturer", type: "text" },
		{ name: "model_number", label: "Model Number", type: "text" },
		{ name: "lot_number", label: "Lot Number", type: "text" },
		{ name: "serial_number", label: "Serial Number", type: "text" },
		{ name: "implant_date", label: "Implant Date", type: "date" },
		{ name: "implanting_provider", label: "Implanting Provider", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "reason", label: "Reason for Implant", type: "textarea" },
		{ name: "body_location", label: "Body Location", type: "text" },
		{
			name: "mri_safe", label: "MRI Safe", type: "dropdown", options: [
				{ label: "Yes", value: "yes" },
				{ label: "No", value: "no" },
				{ label: "Conditional", value: "conditional" },
				{ label: "Unknown", value: "unknown" },
			]
		},
		{ name: "expected_longevity", label: "Expected Longevity", type: "text" },
		{ name: "replacement_due", label: "Replacement Due Date", type: "date" },
		{ name: "revision_history", label: "Revision History", type: "textarea" },
		{ name: "warranty_info", label: "Warranty Information", type: "textarea" },
		{ name: "patient_has_card", label: "Patient Has Implant Card", type: "boolean" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Family History
	family_history: [
		{ name: "condition", label: "Condition", type: "text" },
		{ name: "family_member", label: "Family Member", type: "dropdown", options: commonFields.familyMemberOptions },
		{ name: "relative_name", label: "Relative's Name", type: "text" },
		{ name: "age_at_diagnosis", label: "Age at Diagnosis", type: "number" },
		{ name: "deceased", label: "Deceased", type: "boolean" },
		{ name: "age_at_death", label: "Age at Death", type: "number" },
		{ name: "cause_of_death", label: "Cause of Death", type: "text" },
		{ name: "patient_risk", label: "Impact on Patient Risk", type: "textarea" },
		{ name: "pertinent_negatives", label: "Pertinent Negatives", type: "textarea" },
		{ name: "has_genetic_testing", label: "Has Genetic Testing", type: "boolean" },
		{ name: "genetic_test_results", label: "Genetic Test Results", type: "textarea" },
		{ name: "recommended_screening", label: "Recommended Screening", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Genetic Predisposition
	genetic_predisposition: [
		{ name: "condition", label: "Condition", type: "text" },
		{ name: "gene_mutation", label: "Gene/Mutation", type: "text" },
		{ name: "test_date", label: "Test Date", type: "date" },
		{ name: "testing_facility", label: "Testing Facility", type: "text" },
		{ name: "test_type", label: "Test Type", type: "text" },
		{ name: "test_results", label: "Test Results", type: "textarea" },
		{ name: "interpretation", label: "Clinical Interpretation", type: "textarea" },
		{ name: "risk_percentage", label: "Risk Percentage", type: "number" },
		{ name: "family_history", label: "Family History", type: "textarea" },
		{ name: "preventive_measures", label: "Preventive Measures", type: "textarea" },
		{ name: "recommended_screening", label: "Recommended Screening", type: "textarea" },
		{ name: "counseling_provided", label: "Genetic Counseling Provided", type: "boolean" },
		{ name: "counselor_name", label: "Genetic Counselor Name", type: "text" },
		{ name: "counseling_date", label: "Counseling Date", type: "date" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Immunization
	immunization: [
		{ name: "vaccine_name", label: "Vaccine Name", type: "text" },
		{ name: "disease_targeted", label: "Disease Targeted", type: "text" },
		{ name: "administration_date", label: "Administration Date", type: "date" },
		{ name: "administering_provider", label: "Administering Provider", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "dose_number", label: "Dose Number", type: "number" },
		{ name: "series_complete", label: "Series Complete", type: "boolean" },
		{ name: "manufacturer", label: "Manufacturer", type: "text" },
		{ name: "lot_number", label: "Lot Number", type: "text" },
		{ name: "expiration_date", label: "Expiration Date", type: "date" },
		{
			name: "route", label: "Route", type: "dropdown", options: [
				{ label: "Intramuscular", value: "intramuscular" },
				{ label: "Subcutaneous", value: "subcutaneous" },
				{ label: "Intradermal", value: "intradermal" },
				{ label: "Oral", value: "oral" },
				{ label: "Nasal", value: "nasal" },
				{ label: "Other", value: "other" },
			]
		},
		{
			name: "site", label: "Site", type: "dropdown", options: [
				{ label: "Left Arm", value: "left_arm" },
				{ label: "Right Arm", value: "right_arm" },
				{ label: "Left Thigh", value: "left_thigh" },
				{ label: "Right Thigh", value: "right_thigh" },
				{ label: "Buttock", value: "buttock" },
				{ label: "Oral", value: "oral" },
				{ label: "Nasal", value: "nasal" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "dose", label: "Dose", type: "text" },
		{ name: "funding_source", label: "Funding Source", type: "text" },
		{ name: "adverse_reaction", label: "Adverse Reaction", type: "textarea" },
		{ name: "next_dose_due", label: "Next Dose Due", type: "date" },
		{ name: "cvx_code", label: "CVX Code", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Screening Test
	screening_test: [
		{ name: "test_name", label: "Test Name", type: "text" },
		{ name: "test_date", label: "Test Date", type: "date" },
		{ name: "ordering_provider", label: "Ordering Provider", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "reason", label: "Reason for Test", type: "textarea" },
		{ name: "results", label: "Results", type: "textarea" },
		{ name: "result_value", label: "Result Value", type: "text" },
		{ name: "reference_range", label: "Reference Range", type: "text" },
		{
			name: "interpretation", label: "Interpretation", type: "dropdown", options: [
				{ label: "Normal", value: "normal" },
				{ label: "Abnormal", value: "abnormal" },
				{ label: "Borderline", value: "borderline" },
				{ label: "Inconclusive", value: "inconclusive" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "followup_required", label: "Follow-up Required", type: "boolean" },
		{ name: "followup_type", label: "Follow-up Type", type: "text" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "next_screening_due", label: "Next Screening Due", type: "date" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Preventive Measure
	preventive_measure: [
		{
			name: "measure_type", label: "Measure Type", type: "dropdown", options: [
				{ label: "Counseling", value: "counseling" },
				{ label: "Education", value: "education" },
				{ label: "Prophylaxis", value: "prophylaxis" },
				{ label: "Lifestyle Modification", value: "lifestyle_modification" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "measure_name", label: "Measure Name", type: "text" },
		{ name: "target_condition", label: "Target Condition", type: "text" },
		{ name: "date_provided", label: "Date Provided", type: "date" },
		{ name: "provider", label: "Provider", type: "text" },
		{ name: "description", label: "Description", type: "textarea" },
		{ name: "patient_education_materials", label: "Patient Education Materials", type: "textarea" },
		{
			name: "patient_compliance", label: "Patient Compliance", type: "dropdown", options: [
				{ label: "Good", value: "good" },
				{ label: "Fair", value: "fair" },
				{ label: "Poor", value: "poor" },
				{ label: "Unknown", value: "unknown" },
			]
		},
		{ name: "outcome", label: "Outcome", type: "textarea" },
		{ name: "followup_needed", label: "Follow-up Needed", type: "boolean" },
		{ name: "followup_date", label: "Follow-up Date", type: "date" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Substance Use
	substance_use: [
		{
			name: "substance_type", label: "Substance Type", type: "dropdown", options: [
				{ label: "Tobacco", value: "tobacco" },
				{ label: "Alcohol", value: "alcohol" },
				{ label: "Marijuana", value: "marijuana" },
				{ label: "Opioids", value: "opioids" },
				{ label: "Stimulants", value: "stimulants" },
				{ label: "Sedatives", value: "sedatives" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_substance", label: "Specific Substance", type: "text" },
		{ name: "usage_pattern", label: "Usage Pattern", type: "textarea" },
		{
			name: "frequency", label: "Frequency", type: "dropdown", options: [
				{ label: "Daily", value: "daily" },
				{ label: "Weekly", value: "weekly" },
				{ label: "Monthly", value: "monthly" },
				{ label: "Occasionally", value: "occasionally" },
				{ label: "Rarely", value: "rarely" },
				{ label: "Former User", value: "former" },
			]
		},
		{ name: "quantity", label: "Quantity", type: "text" },
		{ name: "units", label: "Units", type: "text" },
		{
			name: "route", label: "Route of Administration", type: "dropdown", options: [
				{ label: "Oral", value: "oral" },
				{ label: "Smoking", value: "smoking" },
				{ label: "Inhalation", value: "inhalation" },
				{ label: "Injection", value: "injection" },
				{ label: "Nasal", value: "nasal" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date (if quit)", type: "date" },
		{ name: "quit_attempts", label: "Quit Attempts", type: "textarea" },
		{ name: "intervention_history", label: "Intervention History", type: "textarea" },
		{ name: "treatment_program", label: "Current Treatment Program", type: "textarea" },
		{ name: "health_impact", label: "Health Impact", type: "textarea" },
		{ name: "screening_score", label: "Screening Score", type: "text" },
		{ name: "screening_tool", label: "Screening Tool Used", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Environmental Exposure
	environmental_exposure: [
		{
			name: "exposure_type", label: "Exposure Type", type: "dropdown", options: [
				{ label: "Chemical", value: "chemical" },
				{ label: "Biological", value: "biological" },
				{ label: "Physical", value: "physical" },
				{ label: "Radiation", value: "radiation" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "agent", label: "Agent", type: "text" },
		{
			name: "exposure_setting", label: "Exposure Setting", type: "dropdown", options: [
				{ label: "Occupational", value: "occupational" },
				{ label: "Residential", value: "residential" },
				{ label: "Recreational", value: "recreational" },
				{ label: "Accidental", value: "accidental" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "location", label: "Location", type: "text" },
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date", type: "date" },
		{ name: "duration", label: "Duration", type: "text" },
		{ name: "frequency", label: "Frequency", type: "text" },
		{ name: "protection_used", label: "Protection Used", type: "textarea" },
		{ name: "exposure_level", label: "Exposure Level", type: "text" },
		{ name: "measurement_method", label: "Measurement Method", type: "text" },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "health_effects", label: "Health Effects", type: "textarea" },
		{ name: "treatment_received", label: "Treatment Received", type: "textarea" },
		{ name: "reported_to_authorities", label: "Reported to Authorities", type: "boolean" },
		{ name: "report_date", label: "Report Date", type: "date" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Occupational Hazard
	occupational_hazard: [
		{ name: "occupation", label: "Occupation", type: "text" },
		{ name: "employer", label: "Employer", type: "text" },
		{
			name: "hazard_type", label: "Hazard Type", type: "dropdown", options: [
				{ label: "Chemical", value: "chemical" },
				{ label: "Biological", value: "biological" },
				{ label: "Physical", value: "physical" },
				{ label: "Ergonomic", value: "ergonomic" },
				{ label: "Psychosocial", value: "psychosocial" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_hazard", label: "Specific Hazard", type: "text" },
		{ name: "employment_duration", label: "Employment Duration", type: "text" },
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date", type: "date" },
		{ name: "exposure_frequency", label: "Exposure Frequency", type: "text" },
		{ name: "ppe_used", label: "PPE Used", type: "textarea" },
		{ name: "exposure_monitoring", label: "Exposure Monitoring", type: "textarea" },
		{ name: "workplace_controls", label: "Workplace Controls", type: "textarea" },
		{ name: "health_effects", label: "Health Effects", type: "textarea" },
		{ name: "workers_comp_claim", label: "Workers' Comp Claim Filed", type: "boolean" },
		{ name: "claim_date", label: "Claim Date", type: "date" },
		{ name: "claim_status", label: "Claim Status", type: "text" },
		{ name: "workplace_accommodations", label: "Workplace Accommodations", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Travel History
	travel_history: [
		{ name: "destination", label: "Destination", type: "text" },
		{ name: "country", label: "Country", type: "text" },
		{ name: "departure_date", label: "Departure Date", type: "date" },
		{ name: "return_date", label: "Return Date", type: "date" },
		{
			name: "purpose", label: "Purpose", type: "dropdown", options: [
				{ label: "Leisure", value: "leisure" },
				{ label: "Business", value: "business" },
				{ label: "Education", value: "education" },
				{ label: "Medical", value: "medical" },
				{ label: "Humanitarian", value: "humanitarian" },
				{ label: "Military", value: "military" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "accommodation_type", label: "Accommodation Type", type: "text" },
		{ name: "high_risk_activities", label: "High Risk Activities", type: "textarea" },
		{ name: "pre_travel_vaccination", label: "Pre-travel Vaccination", type: "textarea" },
		{ name: "pre_travel_prophylaxis", label: "Pre-travel Prophylaxis", type: "textarea" },
		{ name: "illness_during_travel", label: "Illness During Travel", type: "textarea" },
		{ name: "healthcare_sought_abroad", label: "Healthcare Sought Abroad", type: "textarea" },
		{ name: "post_travel_symptoms", label: "Post-travel Symptoms", type: "textarea" },
		{ name: "post_travel_testing", label: "Post-travel Testing", type: "textarea" },
		{ name: "post_travel_diagnosis", label: "Post-travel Diagnosis", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Mental Health Condition
	mental_health: [
		{ name: "condition", label: "Condition", type: "text" },
		{ name: "diagnosis_date", label: "Diagnosis Date", type: "date" },
		{ name: "diagnosing_provider", label: "Diagnosing Provider", type: "text" },
		{ name: "provider_specialty", label: "Provider Specialty", type: "text" },
		{ name: "diagnosis_method", label: "Diagnosis Method", type: "textarea" },
		{ name: "dsm_code", label: "DSM Code", type: "text" },
		{ name: "icd_code", label: "ICD Code", type: "text" },
		{ name: "status", label: "Current Status", type: "dropdown", options: commonFields.statusOptions },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "triggers", label: "Known Triggers", type: "textarea" },
		{ name: "treatment_history", label: "Treatment History", type: "textarea" },
		{ name: "current_treatment", label: "Current Treatment", type: "textarea" },
		{ name: "medication_history", label: "Medication History", type: "textarea" },
		{ name: "therapy_type", label: "Therapy Type", type: "text" },
		{ name: "hospitalization_history", label: "Hospitalization History", type: "textarea" },
		{ name: "suicidality_history", label: "Suicidality History", type: "textarea" },
		{ name: "current_risk_assessment", label: "Current Risk Assessment", type: "textarea" },
		{ name: "functional_impact", label: "Functional Impact", type: "textarea" },
		{ name: "support_system", label: "Support System", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Psychiatric Treatment
	psychiatric_treatment: [
		{
			name: "treatment_type", label: "Treatment Type", type: "dropdown", options: [
				{ label: "Medication", value: "medication" },
				{ label: "Psychotherapy", value: "psychotherapy" },
				{ label: "ECT", value: "ect" },
				{ label: "TMS", value: "tms" },
				{ label: "Inpatient Program", value: "inpatient" },
				{ label: "Partial Hospitalization", value: "partial" },
				{ label: "Intensive Outpatient", value: "iop" },
				{ label: "Support Group", value: "support_group" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_treatment", label: "Specific Treatment", type: "text" },
		{ name: "targeting_condition", label: "Targeting Condition", type: "text" },
		{ name: "provider", label: "Provider", type: "text" },
		{ name: "provider_specialty", label: "Provider Specialty", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date", type: "date" },
		{ name: "frequency", label: "Frequency", type: "text" },
		{ name: "status", label: "Status", type: "dropdown", options: commonFields.treatmentStatusOptions },
		{
			name: "response", label: "Response", type: "dropdown", options: [
				{ label: "Good", value: "good" },
				{ label: "Partial", value: "partial" },
				{ label: "Poor", value: "poor" },
				{ label: "None", value: "none" },
				{ label: "Adverse Effects", value: "adverse" },
			]
		},
		{ name: "side_effects", label: "Side Effects", type: "textarea" },
		{ name: "reason_for_discontinuation", label: "Reason for Discontinuation", type: "textarea" },
		{ name: "symptom_changes", label: "Symptom Changes", type: "textarea" },
		{ name: "functional_changes", label: "Functional Changes", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Cognitive Assessment
	cognitive_assessment: [
		{
			name: "assessment_type", label: "Assessment Type", type: "dropdown", options: [
				{ label: "Mini-Mental State Exam", value: "mmse" },
				{ label: "Montreal Cognitive Assessment", value: "moca" },
				{ label: "Neuropsychological Testing", value: "neuropsych" },
				{ label: "Dementia Rating Scale", value: "drs" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "assessment_name", label: "Assessment Name", type: "text" },
		{ name: "date", label: "Assessment Date", type: "date" },
		{ name: "administering_provider", label: "Administering Provider", type: "text" },
		{ name: "provider_specialty", label: "Provider Specialty", type: "text" },
		{ name: "reason", label: "Reason for Assessment", type: "textarea" },
		{ name: "score", label: "Score", type: "text" },
		{ name: "maximum_score", label: "Maximum Possible Score", type: "text" },
		{ name: "interpretation", label: "Interpretation", type: "textarea" },
		{ name: "domains_tested", label: "Domains Tested", type: "textarea" },
		{ name: "domain_scores", label: "Domain Scores", type: "textarea" },
		{ name: "cognitive_strengths", label: "Cognitive Strengths", type: "textarea" },
		{ name: "cognitive_weaknesses", label: "Cognitive Weaknesses", type: "textarea" },
		{ name: "comparison_to_baseline", label: "Comparison to Baseline", type: "textarea" },
		{ name: "recommendations", label: "Recommendations", type: "textarea" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Pregnancy
	pregnancy: [
		{ name: "gravida", label: "Gravida", type: "number" },
		{ name: "para", label: "Para", type: "text" },
		{ name: "lmp", label: "Last Menstrual Period", type: "date" },
		{ name: "edd_by_lmp", label: "EDD by LMP", type: "date" },
		{ name: "edd_by_ultrasound", label: "EDD by Ultrasound", type: "date" },
		{ name: "final_edd", label: "Final EDD", type: "date" },
		{
			name: "dating_method", label: "Dating Method", type: "dropdown", options: [
				{ label: "LMP", value: "lmp" },
				{ label: "1st Trimester Ultrasound", value: "first_tri_us" },
				{ label: "2nd Trimester Ultrasound", value: "second_tri_us" },
				{ label: "IVF", value: "ivf" },
				{ label: "Other", value: "other" },
			]
		},
		{
			name: "pregnancy_type", label: "Pregnancy Type", type: "dropdown", options: [
				{ label: "Singleton", value: "singleton" },
				{ label: "Twin", value: "twin" },
				{ label: "Triplet", value: "triplet" },
				{ label: "Higher Order Multiple", value: "higher_multiple" },
			]
		},
		{
			name: "conception_method", label: "Conception Method", type: "dropdown", options: [
				{ label: "Natural", value: "natural" },
				{ label: "IUI", value: "iui" },
				{ label: "IVF", value: "ivf" },
				{ label: "ICSI", value: "icsi" },
				{ label: "Donor Egg", value: "donor_egg" },
				{ label: "Donor Sperm", value: "donor_sperm" },
				{ label: "Surrogacy", value: "surrogacy" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "prenatal_care_started", label: "Prenatal Care Started", type: "date" },
		{ name: "primary_ob_provider", label: "Primary OB Provider", type: "text" },
		{ name: "risk_factors", label: "Risk Factors", type: "textarea" },
		{
			name: "risk_assessment", label: "Risk Assessment", type: "dropdown", options: [
				{ label: "Low Risk", value: "low" },
				{ label: "Moderate Risk", value: "moderate" },
				{ label: "High Risk", value: "high" },
			]
		},
		{ name: "screening_tests", label: "Screening Tests", type: "textarea" },
		{ name: "diagnostic_tests", label: "Diagnostic Tests", type: "textarea" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{
			name: "current_status", label: "Current Status", type: "dropdown", options: [
				{ label: "Ongoing", value: "ongoing" },
				{ label: "Completed", value: "completed" },
				{ label: "Miscarriage", value: "miscarriage" },
				{ label: "Termination", value: "termination" },
				{ label: "Ectopic", value: "ectopic" },
				{ label: "Molar", value: "molar" },
			]
		},
		{ name: "delivery_date", label: "Delivery Date", type: "date" },
		{
			name: "delivery_mode", label: "Delivery Mode", type: "dropdown", options: [
				{ label: "Vaginal", value: "vaginal" },
				{ label: "Cesarean", value: "cesarean" },
				{ label: "VBAC", value: "vbac" },
				{ label: "Operative Vaginal", value: "operative_vaginal" },
			]
		},
		{ name: "delivery_location", label: "Delivery Location", type: "text" },
		{ name: "delivery_provider", label: "Delivery Provider", type: "text" },
		{ name: "delivery_complications", label: "Delivery Complications", type: "textarea" },
		{ name: "postpartum_complications", label: "Postpartum Complications", type: "textarea" },
		{ name: "birth_outcome", label: "Birth Outcome", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Obstetric History
	obstetric_history: [
		{ name: "pregnancy_number", label: "Pregnancy Number", type: "number" },
		{ name: "year", label: "Year", type: "number" },
		{ name: "gestation_weeks", label: "Gestation (Weeks)", type: "number" },
		{
			name: "pregnancy_outcome", label: "Pregnancy Outcome", type: "dropdown", options: [
				{ label: "Live Birth", value: "live_birth" },
				{ label: "Stillbirth", value: "stillbirth" },
				{ label: "Miscarriage", value: "miscarriage" },
				{ label: "Abortion", value: "abortion" },
				{ label: "Ectopic", value: "ectopic" },
				{ label: "Molar", value: "molar" },
			]
		},
		{
			name: "delivery_mode", label: "Delivery Mode", type: "dropdown", options: [
				{ label: "Vaginal", value: "vaginal" },
				{ label: "Cesarean", value: "cesarean" },
				{ label: "VBAC", value: "vbac" },
				{ label: "Operative Vaginal", value: "operative_vaginal" },
				{ label: "N/A", value: "na" },
			]
		},
		{ name: "delivery_location", label: "Delivery Location", type: "text" },
		{ name: "birth_weight", label: "Birth Weight", type: "text" },
		{
			name: "sex", label: "Sex", type: "dropdown", options: [
				{ label: "Male", value: "male" },
				{ label: "Female", value: "female" },
				{ label: "Other", value: "other" },
				{ label: "Unknown", value: "unknown" },
			]
		},
		{ name: "multiple_gestation", label: "Multiple Gestation", type: "boolean" },
		{ name: "number_of_fetuses", label: "Number of Fetuses", type: "number" },
		{
			name: "labor_onset", label: "Labor Onset", type: "dropdown", options: [
				{ label: "Spontaneous", value: "spontaneous" },
				{ label: "Induced", value: "induced" },
				{ label: "Augmented", value: "augmented" },
				{ label: "N/A", value: "na" },
			]
		},
		{ name: "labor_duration", label: "Labor Duration", type: "text" },
		{ name: "anesthesia_type", label: "Anesthesia Type", type: "text" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{ name: "postpartum_complications", label: "Postpartum Complications", type: "textarea" },
		{ name: "child_name", label: "Child's Name", type: "text" },
		{ name: "child_complications", label: "Child Complications", type: "textarea" },
		{ name: "breastfeeding", label: "Breastfeeding", type: "boolean" },
		{ name: "breastfeeding_duration", label: "Breastfeeding Duration", type: "text" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Gynecological Condition
	gynecological_condition: [
		{ name: "condition", label: "Condition", type: "text" },
		{ name: "diagnosis_date", label: "Diagnosis Date", type: "date" },
		{ name: "diagnosing_provider", label: "Diagnosing Provider", type: "text" },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "diagnostic_method", label: "Diagnostic Method", type: "textarea" },
		{ name: "status", label: "Current Status", type: "dropdown", options: commonFields.statusOptions },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "treatment_history", label: "Treatment History", type: "textarea" },
		{ name: "current_treatment", label: "Current Treatment", type: "textarea" },
		{ name: "surgical_intervention", label: "Surgical Intervention", type: "textarea" },
		{ name: "impact_on_fertility", label: "Impact on Fertility", type: "textarea" },
		{ name: "impact_on_quality_of_life", label: "Impact on Quality of Life", type: "textarea" },
		{ name: "monitoring_plan", label: "Monitoring Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Contraception
	contraception: [
		{
			name: "method", label: "Method", type: "dropdown", options: [
				{ label: "Oral Contraceptive", value: "oral" },
				{ label: "IUD", value: "iud" },
				{ label: "Implant", value: "implant" },
				{ label: "Injection", value: "injection" },
				{ label: "Patch", value: "patch" },
				{ label: "Ring", value: "ring" },
				{ label: "Condom", value: "condom" },
				{ label: "Diaphragm", value: "diaphragm" },
				{ label: "Sterilization", value: "sterilization" },
				{ label: "Natural Family Planning", value: "nfp" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_product", label: "Specific Product", type: "text" },
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date", type: "date" },
		{ name: "prescribing_provider", label: "Prescribing Provider", type: "text" },
		{ name: "placement_provider", label: "Placement Provider", type: "text" },
		{ name: "placement_date", label: "Placement Date", type: "date" },
		{ name: "removal_date", label: "Removal Date", type: "date" },
		{ name: "side_effects", label: "Side Effects", type: "textarea" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{
			name: "effectiveness", label: "Effectiveness", type: "dropdown", options: [
				{ label: "Very Effective", value: "very" },
				{ label: "Moderately Effective", value: "moderate" },
				{ label: "Somewhat Effective", value: "somewhat" },
				{ label: "Not Effective", value: "not" },
				{ label: "Resulted in Pregnancy", value: "pregnancy" },
			]
		},
		{ name: "reason_for_discontinuation", label: "Reason for Discontinuation", type: "textarea" },
		{
			name: "patient_satisfaction", label: "Patient Satisfaction", type: "dropdown", options: [
				{ label: "Very Satisfied", value: "very" },
				{ label: "Satisfied", value: "satisfied" },
				{ label: "Neutral", value: "neutral" },
				{ label: "Dissatisfied", value: "dissatisfied" },
				{ label: "Very Dissatisfied", value: "very_dissatisfied" },
			]
		},
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Developmental Milestone
	developmental_milestone: [
		{
			name: "milestone_category", label: "Milestone Category", type: "dropdown", options: [
				{ label: "Motor", value: "motor" },
				{ label: "Language", value: "language" },
				{ label: "Social", value: "social" },
				{ label: "Cognitive", value: "cognitive" },
				{ label: "Adaptive", value: "adaptive" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_milestone", label: "Specific Milestone", type: "text" },
		{ name: "expected_age", label: "Expected Age", type: "text" },
		{ name: "achieved_date", label: "Date Achieved", type: "date" },
		{ name: "child_age_at_achievement", label: "Child's Age at Achievement", type: "text" },
		{
			name: "on_time", label: "On Time", type: "dropdown", options: [
				{ label: "Early", value: "early" },
				{ label: "On Time", value: "on_time" },
				{ label: "Slightly Delayed", value: "slightly_delayed" },
				{ label: "Significantly Delayed", value: "significantly_delayed" },
				{ label: "Not Yet Achieved", value: "not_achieved" },
			]
		},
		{ name: "screening_tool", label: "Screening Tool Used", type: "text" },
		{ name: "screener", label: "Screener", type: "text" },
		{ name: "parental_concern", label: "Parental Concern", type: "textarea" },
		{ name: "provider_concern", label: "Provider Concern", type: "textarea" },
		{ name: "environmental_factors", label: "Environmental Factors", type: "textarea" },
		{ name: "intervention_needed", label: "Intervention Needed", type: "boolean" },
		{ name: "intervention_type", label: "Intervention Type", type: "textarea" },
		{ name: "referral_made", label: "Referral Made", type: "boolean" },
		{ name: "referral_to", label: "Referral To", type: "text" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Congenital Condition
	congenital_condition: [
		{ name: "condition", label: "Condition", type: "text" },
		{
			name: "detection_timing", label: "Detection Timing", type: "dropdown", options: [
				{ label: "Prenatal", value: "prenatal" },
				{ label: "At Birth", value: "at_birth" },
				{ label: "Postnatal", value: "postnatal" },
			]
		},
		{ name: "detection_date", label: "Detection Date", type: "date" },
		{ name: "detection_method", label: "Detection Method", type: "text" },
		{ name: "diagnosing_provider", label: "Diagnosing Provider", type: "text" },
		{ name: "affected_system", label: "Affected Body System", type: "text" },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "genetic_testing", label: "Genetic Testing", type: "boolean" },
		{ name: "genetic_test_results", label: "Genetic Test Results", type: "textarea" },
		{ name: "family_history", label: "Family History", type: "textarea" },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "functional_impact", label: "Functional Impact", type: "textarea" },
		{ name: "treatment_history", label: "Treatment History", type: "textarea" },
		{ name: "current_treatment", label: "Current Treatment", type: "textarea" },
		{ name: "surgical_history", label: "Surgical History", type: "textarea" },
		{ name: "specialists_involved", label: "Specialists Involved", type: "textarea" },
		{ name: "long_term_prognosis", label: "Long-term Prognosis", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Growth Assessment
	growth_assessment: [
		{ name: "assessment_date", label: "Assessment Date", type: "date" },
		{ name: "child_age", label: "Child's Age", type: "text" },
		{ name: "height", label: "Height", type: "text" },
		{ name: "height_percentile", label: "Height Percentile", type: "text" },
		{ name: "weight", label: "Weight", type: "text" },
		{ name: "weight_percentile", label: "Weight Percentile", type: "text" },
		{ name: "bmi", label: "BMI", type: "text" },
		{ name: "bmi_percentile", label: "BMI Percentile", type: "text" },
		{ name: "head_circumference", label: "Head Circumference", type: "text" },
		{ name: "head_circumference_percentile", label: "Head Circumference Percentile", type: "text" },
		{ name: "growth_velocity", label: "Growth Velocity", type: "text" },
		{ name: "growth_chart", label: "Growth Chart Used", type: "text" },
		{
			name: "growth_pattern", label: "Growth Pattern", type: "dropdown", options: [
				{ label: "Normal", value: "normal" },
				{ label: "Accelerated", value: "accelerated" },
				{ label: "Delayed", value: "delayed" },
				{ label: "Failure to Thrive", value: "ftt" },
				{ label: "Obesity", value: "obesity" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "provider_concern", label: "Provider Concern", type: "textarea" },
		{ name: "parental_concern", label: "Parental Concern", type: "textarea" },
		{ name: "dietary_intake", label: "Dietary Intake", type: "textarea" },
		{ name: "activity_level", label: "Activity Level", type: "textarea" },
		{ name: "environmental_factors", label: "Environmental Factors", type: "textarea" },
		{ name: "underlying_conditions", label: "Underlying Conditions", type: "textarea" },
		{ name: "intervention_needed", label: "Intervention Needed", type: "boolean" },
		{ name: "intervention_plan", label: "Intervention Plan", type: "textarea" },
		{ name: "referral_made", label: "Referral Made", type: "boolean" },
		{ name: "referral_to", label: "Referral To", type: "text" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Trauma/Injury
	trauma_injury: [
		{
			name: "injury_type", label: "Injury Type", type: "dropdown", options: [
				{ label: "Fracture", value: "fracture" },
				{ label: "Laceration", value: "laceration" },
				{ label: "Contusion", value: "contusion" },
				{ label: "Sprain/Strain", value: "sprain_strain" },
				{ label: "Burn", value: "burn" },
				{ label: "Concussion/TBI", value: "concussion_tbi" },
				{ label: "Crush Injury", value: "crush" },
				{ label: "Penetrating Injury", value: "penetrating" },
				{ label: "Multi-trauma", value: "multi_trauma" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_injury", label: "Specific Injury", type: "text" },
		{ name: "date_of_injury", label: "Date of Injury", type: "date" },
		{ name: "mechanism", label: "Mechanism of Injury", type: "textarea" },
		{ name: "location", label: "Location Where Injury Occurred", type: "text" },
		{ name: "body_site", label: "Body Site", type: "text" },
		{ name: "severity", label: "Severity", type: "dropdown", options: commonFields.severityOptions },
		{ name: "initial_treatment", label: "Initial Treatment", type: "textarea" },
		{ name: "treatment_facility", label: "Treatment Facility", type: "text" },
		{ name: "imaging_performed", label: "Imaging Performed", type: "textarea" },
		{ name: "imaging_results", label: "Imaging Results", type: "textarea" },
		{ name: "surgical_intervention", label: "Surgical Intervention", type: "textarea" },
		{ name: "rehabilitation", label: "Rehabilitation", type: "textarea" },
		{
			name: "recovery_status", label: "Recovery Status", type: "dropdown", options: [
				{ label: "Complete Recovery", value: "complete" },
				{ label: "Partial Recovery", value: "partial" },
				{ label: "Ongoing Recovery", value: "ongoing" },
				{ label: "Permanent Disability", value: "permanent" },
			]
		},
		{ name: "functional_limitation", label: "Functional Limitation", type: "textarea" },
		{ name: "work_school_status", label: "Work/School Status", type: "textarea" },
		{ name: "workers_comp_case", label: "Workers' Comp Case", type: "boolean" },
		{ name: "legal_case", label: "Legal Case", type: "boolean" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Hospitalization
	hospitalization: [
		{ name: "admission_date", label: "Admission Date", type: "date" },
		{ name: "discharge_date", label: "Discharge Date", type: "date" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "department", label: "Department/Unit", type: "text" },
		{
			name: "admission_type", label: "Admission Type", type: "dropdown", options: [
				{ label: "Emergency", value: "emergency" },
				{ label: "Planned", value: "planned" },
				{ label: "Transfer", value: "transfer" },
				{ label: "Observation", value: "observation" },
			]
		},
		{ name: "admission_reason", label: "Reason for Admission", type: "textarea" },
		{ name: "admitting_provider", label: "Admitting Provider", type: "text" },
		{ name: "attending_provider", label: "Attending Provider", type: "text" },
		{ name: "consulting_providers", label: "Consulting Providers", type: "textarea" },
		{ name: "primary_diagnosis", label: "Primary Diagnosis", type: "text" },
		{ name: "secondary_diagnoses", label: "Secondary Diagnoses", type: "textarea" },
		{ name: "procedures_performed", label: "Procedures Performed", type: "textarea" },
		{ name: "medications_during_stay", label: "Medications During Stay", type: "textarea" },
		{ name: "icu_stay", label: "ICU Stay", type: "boolean" },
		{ name: "icu_days", label: "Days in ICU", type: "number" },
		{ name: "ventilator_support", label: "Ventilator Support", type: "boolean" },
		{ name: "ventilator_days", label: "Days on Ventilator", type: "number" },
		{ name: "complications", label: "Complications", type: "textarea" },
		{ name: "discharge_condition", label: "Condition at Discharge", type: "textarea" },
		{
			name: "discharge_disposition", label: "Discharge Disposition", type: "dropdown", options: [
				{ label: "Home", value: "home" },
				{ label: "Home with Home Health", value: "home_health" },
				{ label: "Skilled Nursing Facility", value: "snf" },
				{ label: "Rehabilitation Facility", value: "rehab" },
				{ label: "Long-term Care Facility", value: "ltc" },
				{ label: "Transfer to Another Hospital", value: "transfer" },
				{ label: "Left Against Medical Advice", value: "ama" },
				{ label: "Expired", value: "expired" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "discharge_medications", label: "Discharge Medications", type: "textarea" },
		{ name: "discharge_instructions", label: "Discharge Instructions", type: "textarea" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "readmission", label: "Readmission", type: "boolean" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Emergency Visit
	emergency_visit: [
		{ name: "visit_date", label: "Visit Date", type: "date" },
		{ name: "facility", label: "Facility", type: "text" },
		{
			name: "arrival_method", label: "Arrival Method", type: "dropdown", options: [
				{ label: "Self-transport", value: "self" },
				{ label: "Ground Ambulance", value: "ground_ambulance" },
				{ label: "Air Ambulance", value: "air_ambulance" },
				{ label: "Police", value: "police" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "chief_complaint", label: "Chief Complaint", type: "textarea" },
		{
			name: "triage_category", label: "Triage Category", type: "dropdown", options: [
				{ label: "Immediate (1)", value: "1" },
				{ label: "Very Urgent (2)", value: "2" },
				{ label: "Urgent (3)", value: "3" },
				{ label: "Standard (4)", value: "4" },
				{ label: "Non-urgent (5)", value: "5" },
			]
		},
		{ name: "initial_vital_signs", label: "Initial Vital Signs", type: "textarea" },
		{ name: "symptoms", label: "Symptoms", type: "textarea" },
		{ name: "diagnostic_tests", label: "Diagnostic Tests", type: "textarea" },
		{ name: "imaging_studies", label: "Imaging Studies", type: "textarea" },
		{ name: "procedures", label: "Procedures Performed", type: "textarea" },
		{ name: "medications_administered", label: "Medications Administered", type: "textarea" },
		{ name: "consultations", label: "Consultations", type: "textarea" },
		{ name: "diagnosis", label: "Diagnosis", type: "textarea" },
		{ name: "treatment", label: "Treatment", type: "textarea" },
		{ name: "length_of_stay", label: "Length of Stay (hours)", type: "number" },
		{
			name: "disposition", label: "Disposition", type: "dropdown", options: [
				{ label: "Discharged Home", value: "discharged" },
				{ label: "Admitted to Hospital", value: "admitted" },
				{ label: "Transferred", value: "transferred" },
				{ label: "Left AMA", value: "ama" },
				{ label: "Expired", value: "expired" },
				{ label: "Observation", value: "observation" },
			]
		},
		{ name: "discharge_condition", label: "Condition at Discharge", type: "textarea" },
		{ name: "discharge_instructions", label: "Discharge Instructions", type: "textarea" },
		{ name: "followup_instructions", label: "Follow-up Instructions", type: "textarea" },
		{ name: "return_precautions", label: "Return Precautions", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Functional Assessment
	functional_assessment: [
		{ name: "assessment_date", label: "Assessment Date", type: "date" },
		{
			name: "assessment_type", label: "Assessment Type", type: "dropdown", options: [
				{ label: "ADL", value: "adl" },
				{ label: "IADL", value: "iadl" },
				{ label: "Physical Therapy", value: "pt" },
				{ label: "Occupational Therapy", value: "ot" },
				{ label: "Speech Therapy", value: "speech" },
				{ label: "Mobility", value: "mobility" },
				{ label: "Fall Risk", value: "fall_risk" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_assessment", label: "Specific Assessment", type: "text" },
		{ name: "assessor", label: "Assessor", type: "text" },
		{ name: "assessor_title", label: "Assessor Title", type: "text" },
		{ name: "reason", label: "Reason for Assessment", type: "textarea" },
		{ name: "score", label: "Score", type: "text" },
		{ name: "interpretation", label: "Interpretation", type: "textarea" },
		{ name: "areas_of_independence", label: "Areas of Independence", type: "textarea" },
		{ name: "areas_requiring_assistance", label: "Areas Requiring Assistance", type: "textarea" },
		{ name: "assistive_devices", label: "Assistive Devices", type: "textarea" },
		{ name: "environmental_modifications", label: "Environmental Modifications", type: "textarea" },
		{ name: "caregiver_needs", label: "Caregiver Needs", type: "textarea" },
		{ name: "intervention_plan", label: "Intervention Plan", type: "textarea" },
		{ name: "goals", label: "Goals", type: "textarea" },
		{ name: "progress", label: "Progress from Previous Assessment", type: "textarea" },
		{ name: "referrals", label: "Referrals", type: "textarea" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Nutritional Assessment
	nutritional_assessment: [
		{ name: "assessment_date", label: "Assessment Date", type: "date" },
		{ name: "assessor", label: "Assessor", type: "text" },
		{ name: "assessor_title", label: "Assessor Title", type: "text" },
		{ name: "height", label: "Height", type: "text" },
		{ name: "weight", label: "Weight", type: "text" },
		{ name: "bmi", label: "BMI", type: "text" },
		{
			name: "weight_category", label: "Weight Category", type: "dropdown", options: [
				{ label: "Underweight", value: "underweight" },
				{ label: "Normal", value: "normal" },
				{ label: "Overweight", value: "overweight" },
				{ label: "Obese", value: "obese" },
				{ label: "Severely Obese", value: "severely_obese" },
			]
		},
		{ name: "weight_change", label: "Recent Weight Change", type: "textarea" },
		{ name: "waist_circumference", label: "Waist Circumference", type: "text" },
		{ name: "body_composition", label: "Body Composition", type: "textarea" },
		{ name: "nutritional_screening_tool", label: "Nutritional Screening Tool", type: "text" },
		{ name: "screening_score", label: "Screening Score", type: "text" },
		{
			name: "nutritional_risk", label: "Nutritional Risk", type: "dropdown", options: [
				{ label: "Low Risk", value: "low" },
				{ label: "Moderate Risk", value: "moderate" },
				{ label: "High Risk", value: "high" },
				{ label: "Malnourished", value: "malnourished" },
			]
		},
		{ name: "diet_history", label: "Diet History", type: "textarea" },
		{ name: "food_allergies", label: "Food Allergies/Intolerances", type: "textarea" },
		{ name: "dietary_restrictions", label: "Dietary Restrictions", type: "textarea" },
		{
			name: "appetite", label: "Appetite", type: "dropdown", options: [
				{ label: "Good", value: "good" },
				{ label: "Fair", value: "fair" },
				{ label: "Poor", value: "poor" },
			]
		},
		{ name: "eating_patterns", label: "Eating Patterns", type: "textarea" },
		{ name: "feeding_ability", label: "Feeding Ability", type: "textarea" },
		{ name: "swallowing_ability", label: "Swallowing Ability", type: "textarea" },
		{ name: "gi_symptoms", label: "GI Symptoms", type: "textarea" },
		{ name: "dental_status", label: "Dental Status", type: "textarea" },
		{ name: "lab_values", label: "Relevant Lab Values", type: "textarea" },
		{ name: "vitamin_deficiencies", label: "Vitamin/Mineral Deficiencies", type: "textarea" },
		{ name: "supplement_use", label: "Supplement Use", type: "textarea" },
		{ name: "nutrition_diagnosis", label: "Nutrition Diagnosis", type: "textarea" },
		{ name: "intervention_plan", label: "Intervention Plan", type: "textarea" },
		{ name: "goals", label: "Goals", type: "textarea" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Sleep Assessment
	sleep_assessment: [
		{ name: "assessment_date", label: "Assessment Date", type: "date" },
		{ name: "assessor", label: "Assessor", type: "text" },
		{
			name: "assessment_method", label: "Assessment Method", type: "dropdown", options: [
				{ label: "Self-report", value: "self_report" },
				{ label: "Sleep Diary", value: "sleep_diary" },
				{ label: "Sleep Study", value: "sleep_study" },
				{ label: "Actigraphy", value: "actigraphy" },
				{ label: "Questionnaire", value: "questionnaire" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "screening_tool", label: "Screening Tool", type: "text" },
		{ name: "screening_score", label: "Screening Score", type: "text" },
		{ name: "typical_bedtime", label: "Typical Bedtime", type: "text" },
		{ name: "typical_wake_time", label: "Typical Wake Time", type: "text" },
		{ name: "sleep_latency", label: "Sleep Latency (minutes)", type: "number" },
		{ name: "total_sleep_time", label: "Total Sleep Time (hours)", type: "number" },
		{ name: "sleep_efficiency", label: "Sleep Efficiency (%)", type: "number" },
		{ name: "awakenings", label: "Number of Awakenings", type: "number" },
		{
			name: "sleep_quality", label: "Sleep Quality", type: "dropdown", options: [
				{ label: "Very Good", value: "very_good" },
				{ label: "Good", value: "good" },
				{ label: "Fair", value: "fair" },
				{ label: "Poor", value: "poor" },
				{ label: "Very Poor", value: "very_poor" },
			]
		},
		{
			name: "daytime_sleepiness", label: "Daytime Sleepiness", type: "dropdown", options: [
				{ label: "None", value: "none" },
				{ label: "Mild", value: "mild" },
				{ label: "Moderate", value: "moderate" },
				{ label: "Severe", value: "severe" },
			]
		},
		{ name: "epworth_score", label: "Epworth Sleepiness Score", type: "number" },
		{ name: "snoring", label: "Snoring", type: "boolean" },
		{ name: "witnessed_apneas", label: "Witnessed Apneas", type: "boolean" },
		{ name: "restless_legs", label: "Restless Legs", type: "boolean" },
		{ name: "sleep_environment", label: "Sleep Environment", type: "textarea" },
		{ name: "sleep_hygiene", label: "Sleep Hygiene Practices", type: "textarea" },
		{ name: "sleep_aids", label: "Sleep Aids Used", type: "textarea" },
		{ name: "caffeine_use", label: "Caffeine Use", type: "textarea" },
		{ name: "alcohol_use", label: "Alcohol Use", type: "textarea" },
		{ name: "sleep_concerns", label: "Sleep Concerns", type: "textarea" },
		{ name: "impact_on_functioning", label: "Impact on Functioning", type: "textarea" },
		{ name: "sleep_diagnosis", label: "Sleep Diagnosis", type: "textarea" },
		{ name: "intervention_plan", label: "Intervention Plan", type: "textarea" },
		{ name: "sleep_study_recommended", label: "Sleep Study Recommended", type: "boolean" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Pain Assessment
	pain_assessment: [
		{ name: "assessment_date", label: "Assessment Date", type: "date" },
		{ name: "assessor", label: "Assessor", type: "text" },
		{ name: "pain_location", label: "Pain Location", type: "text" },
		{ name: "pain_radiation", label: "Pain Radiation", type: "textarea" },
		{
			name: "pain_onset", label: "Pain Onset", type: "dropdown", options: [
				{ label: "Sudden", value: "sudden" },
				{ label: "Gradual", value: "gradual" },
				{ label: "Traumatic", value: "traumatic" },
				{ label: "Unknown", value: "unknown" },
			]
		},
		{
			name: "pain_duration", label: "Pain Duration", type: "dropdown", options: [
				{ label: "Acute (<3 months)", value: "acute" },
				{ label: "Chronic (>3 months)", value: "chronic" },
				{ label: "Intermittent", value: "intermittent" },
				{ label: "Constant", value: "constant" },
			]
		},
		{ name: "pain_timing", label: "Pain Timing/Pattern", type: "textarea" },
		{ name: "pain_intensity", label: "Pain Intensity (0-10)", type: "number" },
		{
			name: "pain_scale_used", label: "Pain Scale Used", type: "dropdown", options: [
				{ label: "Numeric Rating Scale", value: "numeric" },
				{ label: "Visual Analog Scale", value: "vas" },
				{ label: "Wong-Baker FACES", value: "faces" },
				{ label: "FLACC", value: "flacc" },
				{ label: "Other", value: "other" },
			]
		},
		{
			name: "pain_quality", label: "Pain Quality", type: "dropdown", options: [
				{ label: "Sharp", value: "sharp" },
				{ label: "Dull", value: "dull" },
				{ label: "Burning", value: "burning" },
				{ label: "Shooting", value: "shooting" },
				{ label: "Throbbing", value: "throbbing" },
				{ label: "Aching", value: "aching" },
				{ label: "Pressure", value: "pressure" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "aggravating_factors", label: "Aggravating Factors", type: "textarea" },
		{ name: "relieving_factors", label: "Relieving Factors", type: "textarea" },
		{ name: "previous_treatments", label: "Previous Treatments", type: "textarea" },
		{ name: "current_treatments", label: "Current Treatments", type: "textarea" },
		{ name: "treatment_effectiveness", label: "Treatment Effectiveness", type: "textarea" },
		{ name: "pain_impact", label: "Impact on Functioning", type: "textarea" },
		{ name: "pain_coping", label: "Pain Coping Strategies", type: "textarea" },
		{ name: "psychological_factors", label: "Psychological Factors", type: "textarea" },
		{ name: "pain_diagnosis", label: "Pain Diagnosis", type: "textarea" },
		{ name: "pain_etiology", label: "Pain Etiology", type: "textarea" },
		{ name: "intervention_plan", label: "Intervention Plan", type: "textarea" },
		{ name: "medication_changes", label: "Medication Changes", type: "textarea" },
		{ name: "non_pharmacological_approaches", label: "Non-pharmacological Approaches", type: "textarea" },
		{ name: "referrals", label: "Referrals", type: "textarea" },
		{ name: "goals", label: "Goals", type: "textarea" },
		{ name: "followup_plan", label: "Follow-up Plan", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],

	// Rehabilitation
	rehabilitation: [
		{
			name: "rehab_type", label: "Rehabilitation Type", type: "dropdown", options: [
				{ label: "Physical Therapy", value: "pt" },
				{ label: "Occupational Therapy", value: "ot" },
				{ label: "Speech Therapy", value: "speech" },
				{ label: "Cardiac Rehab", value: "cardiac" },
				{ label: "Pulmonary Rehab", value: "pulmonary" },
				{ label: "Neurological Rehab", value: "neuro" },
				{ label: "Other", value: "other" },
			]
		},
		{ name: "specific_rehab", label: "Specific Rehabilitation", type: "text" },
		{ name: "condition", label: "Condition Being Treated", type: "text" },
		{ name: "referral_date", label: "Referral Date", type: "date" },
		{ name: "referring_provider", label: "Referring Provider", type: "text" },
		{ name: "therapy_provider", label: "Therapy Provider", type: "text" },
		{ name: "facility", label: "Facility", type: "text" },
		{ name: "start_date", label: "Start Date", type: "date" },
		{ name: "end_date", label: "End Date", type: "date" },
		{ name: "frequency", label: "Frequency", type: "text" },
		{ name: "initial_evaluation", label: "Initial Evaluation", type: "textarea" },
		{ name: "baseline_function", label: "Baseline Function", type: "textarea" },
		{ name: "treatment_plan", label: "Treatment Plan", type: "textarea" },
		{ name: "goals", label: "Goals", type: "textarea" },
		{ name: "interventions", label: "Interventions", type: "textarea" },
		{ name: "progress", label: "Progress", type: "textarea" },
		{ name: "current_function", label: "Current Function", type: "textarea" },
		{ name: "goal_achievement", label: "Goal Achievement", type: "textarea" },
		{ name: "home_program", label: "Home Program", type: "textarea" },
		{ name: "assistive_devices", label: "Assistive Devices", type: "textarea" },
		{
			name: "patient_compliance", label: "Patient Compliance", type: "dropdown", options: [
				{ label: "Excellent", value: "excellent" },
				{ label: "Good", value: "good" },
				{ label: "Fair", value: "fair" },
				{ label: "Poor", value: "poor" },
			]
		},
		{ name: "barriers", label: "Barriers to Progress", type: "textarea" },
		{ name: "insurance_coverage", label: "Insurance Coverage", type: "textarea" },
		{ name: "authorized_visits", label: "Authorized Visits", type: "number" },
		{ name: "visits_used", label: "Visits Used", type: "number" },
		{ name: "discharge_status", label: "Discharge Status", type: "textarea" },
		{ name: "followup_recommendations", label: "Follow-up Recommendations", type: "textarea" },
		{ name: "notes", label: "Additional Notes", type: "textarea" },
	],
};